// Public leagues — list and detail/registration (Phase 1b).
//
// Registration goes through the createLeagueRegistration callable, never a
// direct write. Rules would permit the client to create its own row, but
// capacity and price cannot be settled in a browser: two people racing for the
// last spot would both read "space available", and a client that names its own
// amount names its own price.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { resolveMember } from '../utils/resolveMember';
import Layout from '../components/Layout';
import {
  Box, Container, Typography, Card, CardContent, Button, Chip, Grid, Paper,
  CircularProgress, Alert, TextField, Divider, MenuItem, Checkbox, FormControlLabel, Link,
  Switch, Tooltip,
} from '@mui/material';
import FrontHandIcon from '@mui/icons-material/FrontHand';
import { logError } from '../utils/errorLogger';
import { isMemberInGoodStanding, hasJuniorWithSessions } from '../utils/membership';
import { computeStandings, applyStandingsOverride, isPlayed, fmtPointRatio } from '../utils/leagueStandings';
import { groupRounds, roundName, winnerOf, isBye } from '../utils/leaguePlayoff';
import { describeLeagueFormat } from '../utils/leagueFormat';
import { loadRequiredWaiver, waiverHref } from '../utils/waiver';
import { isJuniorLeague, missingJuniorFields, ageOn } from '../utils/juniors';
import { localToday } from '../utils/dates';
import { leagueTypeLabel } from '../utils/leagueTypes';
import { GENDERS, impliedGender, genderLabel } from '../utils/leagueGender';
import BracketConnector, { BRACKET_GAP } from '../components/BracketConnector';
import {
  FREE_AGENT, NEW_TEAM, JOIN_TEAM, defaultTeamName, teamChoiceFields,
} from '../utils/leagueTeamChoice';

const parseLocal = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const fmtTime = (t) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || ''));
  if (!m) return '';
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
};

const fmtDate = (iso) => {
  const d = parseLocal(iso);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '–';
};

export function LeaguesList() {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Single-field where + orderBy on the same field needs no composite index.
        const snap = await getDocs(query(collection(db, 'leagues'), orderBy('startDate', 'desc')));
        setLeagues(snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(l => ['registration', 'active', 'playoffs'].includes(l.status)));
      } catch (e) {
        logError('Leagues', e.message, e.stack);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>Leagues</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : leagues.length === 0 ? (
          <Alert severity="info">No leagues are open right now. Check back soon.</Alert>
        ) : (
          <Grid container spacing={2}>
            {leagues.map(l => (
              <Grid size={{ xs: 12, sm: 6 }} key={l.id}>
                <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
                      <Typography variant="h6" fontWeight="bold">{l.name}</Typography>
                      <Chip size="small" label={l.status === 'registration' ? 'Open' : l.status} />
                    </Box>
                    {l.dayTimeDisplay && (
                      <Typography variant="body2" color="text.secondary">{l.dayTimeDisplay}</Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {l.format}s · {l.numWeeks} weeks · starts {fmtDate(l.startDate)}
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 1 }}>
                      ${((Number(l.price) || 0) / 100).toFixed(2)}
                      <Typography component="span" variant="caption" color="text.secondary"> / player</Typography>
                    </Typography>
                    <Button variant="contained" fullWidth sx={{ mt: 2 }}
                      disabled={l.status !== 'registration'}
                      onClick={() => navigate(`/leagues/${l.id}`)}>
                      {l.status === 'registration' ? 'View & Register' : 'View'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Layout>
  );
}

export function LeagueDetail() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [phone, setPhone] = useState('');
  // Prefilled from the account and editable. It decides where the confirmation
  // and receipt land, never who this is — identity is the uid they signed in
  // with, which is what the server prices from.
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  // Junior leagues only. Held whatever the league turns out to be, so switching
  // leagues can't leave a half-filled guardian on screen.
  const [guardianName, setGuardianName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  // Start a team, join one, or be placed. Free agent stays the default: it is
  // the answer that needs no further decision, and the server records it
  // explicitly so an unplaced player is a choice rather than a blank.
  const [teamChoice, setTeamChoice] = useState(FREE_AGENT);
  const [joinTeamId, setJoinTeamId] = useState('');
  const [gender, setGender] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [teamOptions, setTeamOptions] = useState([]);
  const [waiver, setWaiver] = useState(null);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => onAuthStateChanged(auth, setUser), []);
  // Waits for the league: it may name its own waiver instead of the club one.
  useEffect(() => { if (league) loadRequiredWaiver(league).then(setWaiver); }, [league]);

  useEffect(() => {
    (async () => {
      try {
        const s = await getDoc(doc(db, 'leagues', leagueId));
        if (s.exists()) setLeague({ id: s.id, ...s.data() });
      } catch (e) { logError('Leagues', e.message, e.stack); }
      setLoading(false);
    })();
  }, [leagueId]);

  // Standings, once anything has been played. Read-only here — results are
  // entered by an admin on the schedule page.
  //
  // Season fixtures only, and the admin's manual order on top, exactly as the
  // schedule page does it. Counting play-off results here would show players a
  // different table from the one the play-off was seeded from.
  const [standings, setStandings] = useState([]);
  // The play-off, read-only. Drawn and scored by an admin on the schedule page;
  // players see that stored bracket rather than one recomputed here.
  const [playoff, setPlayoff] = useState([]);
  useEffect(() => {
    Promise.all([
      getDocs(query(collection(db, 'leagueTeams'), where('leagueId', '==', leagueId))),
      getDocs(query(collection(db, 'leagueMatches'), where('leagueId', '==', leagueId))),
    ]).then(([t, m]) => {
      const teams = t.docs.map(d => ({ id: d.id, ...d.data() }));
      const all = m.docs.map(d => ({ id: d.id, ...d.data() }));
      const matches = all.filter(x => x.stage !== 'playoff');
      setStandings(matches.some(isPlayed)
        ? applyStandingsOverride(computeStandings(teams, matches), league?.standingsOverride)
        : []);
      setPlayoff(all.filter(x => x.stage === 'playoff'));
      setTeamOptions(teams.slice().sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true })));
    }).catch(() => { setStandings([]); setPlayoff([]); });
  }, [leagueId, league?.standingsOverride]);

  const { rounds: bracketRounds, totalRounds } = groupRounds(playoff);
  const champion = totalRounds > 0 ? winnerOf(bracketRounds[totalRounds - 1][0]) : null;

  // Shown only so the price on screen matches what the server will charge.
  // The rate itself is decided in createLeagueRegistration — this is display.
  const [isMember, setIsMember] = useState(false);
  // Shown as the name their team gets if they start one and leave the box
  // blank. The server derives the same thing from the account, so this is a
  // preview of what will happen rather than a value the browser decides.
  const [myName, setMyName] = useState('');
  // Which junior earned the rate, when that is how it was earned — named on
  // screen so a discounted price is explained rather than mysterious.
  const [juniorBasis, setJuniorBasis] = useState(null);
  // A junior league is entered BY a parent FOR a child, so the player has to be
  // named. The account's own name was used until 2026-09-25, which is why this
  // league's roster read as a list of mothers.
  const [myJuniors, setMyJuniors] = useState([]);
  const [playerAthleteId, setPlayerAthleteId] = useState('');
  const [playerFirst, setPlayerFirst] = useState('');
  // Asked only for a player the club has no record of, because an athlete
  // record is about to be created for them and a coach needs somebody to call.
  const [ecName, setEcName] = useState('');
  const [ecPhone, setEcPhone] = useState('');
  const [allergies, setAllergies] = useState('');
  const [playerLast, setPlayerLast] = useState('');
  useEffect(() => {
    if (!user) { setIsMember(false); setMyName(''); setJuniorBasis(null); return; }
    // Canonical: a merged login's own doc has no membership on it, so a paid
    // member was told they were not one.
    resolveMember(db, user)
      .then(async ({ data }) => {
        const u = data;
        const adult = isMemberInGoodStanding(u);
        setIsMember(adult);
        setMyName(defaultTeamName(u?.firstName, u?.lastName));
        const acct = u?.email || user.email || '';
        if (acct) setEmail(e => e || acct);
        // Only asked when it could change the price, matching the server.
        const jr = adult ? null : await hasJuniorWithSessions(user.uid);
        setJuniorBasis(jr);
        if (jr) setIsMember(true);
        // Prefill from the account. Asking a signed-in member to retype a
        // number we already hold is the sort of thing that makes signing in
        // feel worse than not bothering.
        if (u?.phone) setPhone(p => p || String(u.phone));
        // The juniors on this account, to pick from. Read here rather than
        // typed, so the registration points at the athlete record and the
        // roster can name the player.
        try {
          const [byParent, byArray] = await Promise.all([
            getDocs(query(collection(db, 'athletes'), where('parentUserId', '==', user.uid))),
            getDocs(query(collection(db, 'athletes'), where('parentUserIds', 'array-contains', user.uid))),
          ]);
          const byId = new Map();
          [...byParent.docs, ...byArray.docs].forEach(d => {
            const a = d.data();
            if (a.athleteType === 'junior' && a.isArchived !== true) byId.set(d.id, { id: d.id, ...a });
          });
          const kids = [...byId.values()]
            .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || '')));
          setMyJuniors(kids);
          // One child is the common case, and picking from a list of one is a
          // step that only exists to be clicked.
          if (kids.length === 1) setPlayerAthleteId(kids[0].id);
          // The guardian is whoever holds the account.
          setGuardianName(g => g || `${u?.firstName || ''} ${u?.lastName || ''}`.trim());
        } catch (e) { /* the typed-name fallback covers this */ }
      })
      .catch(() => setIsMember(false));
  }, [user]);

  // The viewer's own registration, so they can see the team they were put on.
  // Rules allow any signed-in user to read leagueRegistrations; this filters to
  // their own rather than relying on that.
  const [myReg, setMyReg] = useState(null);
  useEffect(() => {
    if (!user) { setMyReg(null); return; }
    // Asked of the server, which matches the signed-in address as well as the
    // uid: a junior who registered through the public form has a mini_ id on the
    // row, so a uid-only lookup showed their parent nothing — no team, no
    // wait-list place, no way to pay, and the sign-up form again, which is how
    // one family registered twice. The rules keep these rows to userId == uid
    // because they carry a minor's date of birth, so the address match cannot
    // live in a client query; see myLeagueRegistration.
    httpsCallable(getFunctions(), 'myLeagueRegistration')({ leagueId })
      .then(({ data }) => setMyReg(data?.registration || null))
      .catch(e => logError('Leagues', e.message, e.stack));
  }, [user, leagueId, result]);

  // The team this viewer captains, if any, plus who is on it. Captaincy lives
  // on the team rather than being inferred from "registered first", so it
  // survives an admin moving people around.
  const [myTeam, setMyTeam] = useState(null);
  const [teamMates, setTeamMates] = useState([]);
  const [teamBusy, setTeamBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);

  // Abandoning the checkout page leaves a real registration unpaid, and the
  // page used to say "contact the club" with nothing behind it. This mints a
  // fresh checkout for the registration that already exists — the price comes
  // off the row server-side, so the rate they signed up at is the rate they pay.
  const payNow = async () => {
    setPayBusy(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'createLeaguePaymentSession');
      const { data } = await fn({ registrationId: myReg.id });
      if (data?.url) window.location.href = data.url;
      else setError('Could not start the payment. Please contact the club.');
    } catch (e) {
      setError(e.message || 'Could not start the payment. Please contact the club.');
    }
    setPayBusy(false);
  };
  const [teamMsg, setTeamMsg] = useState('');
  const [renaming, setRenaming] = useState('');
  // Public roster, from a callable rather than a Firestore read — see the
  // section below and the rule on leagueRegistrations for why.
  const [roster, setRoster] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');

  // Accept or decline a captain's invitation. Goes through manageLeagueTeam,
  // which re-checks the roster is still not full — the invitation may have sat
  // unanswered while the team filled up.
  const answerInvite = async (action) => {
    if (!myReg?.pendingTeamId) return;
    setInviteBusy(true); setInviteMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'manageLeagueTeam');
      const { data } = await fn({ teamId: myReg.pendingTeamId, action, registrationId: myReg.id });
      setInviteMsg(data.declined
        ? 'Turned down. You are still on the available players list.'
        : `You're on ${data.joined}.`);
      setResult(r => ({ ...(r || {}), invitedAnswered: Date.now() }));
    } catch (e) {
      setInviteMsg(e.message || 'That did not work. Please try again.');
    }
    setInviteBusy(false);
  };
  const reloadTeam = React.useCallback(async () => {
    if (!user) { setMyTeam(null); setTeamMates([]); return; }
    try {
      const t = await getDocs(query(collection(db, 'leagueTeams'),
        where('leagueId', '==', leagueId), where('captainUserId', '==', user.uid)));
      if (t.empty) { setMyTeam(null); setTeamMates([]); return; }
      const team = { id: t.docs[0].id, ...t.docs[0].data() };
      setMyTeam(team);
      setRenaming(team.name || '');
      // The roster comes from the server, which returns names only. Reading the
      // registrations directly would hand a captain every team-mate's contact
      // details, and on a junior league their date of birth and guardian too.
      const fn = httpsCallable(getFunctions(), 'manageLeagueTeam');
      const { data } = await fn({ teamId: team.id, action: 'roster' });
      setTeamMates(data.members || []);
    } catch (e) {
      // A captain who cannot load the roster still gets the rename control.
      logError('Leagues', e.message, e.stack);
      setTeamMates([]);
    }
  }, [user, leagueId]);
  useEffect(() => { reloadTeam(); }, [reloadTeam, result]);

  // Reloaded on `result` too: registering is the most likely reason the page's
  // own reader is now missing from the list they are looking at.
  useEffect(() => {
    if (!leagueId) return;
    let cancelled = false;
    (async () => {
      try {
        const fn = httpsCallable(getFunctions(), 'leaguePublicRoster');
        const { data } = await fn({ leagueId });
        if (!cancelled) setRoster(data);
      } catch (e) {
        // A draft league or a blip — the page is still worth showing without it.
        if (!cancelled) setRoster(null);
      }
    })();
    return () => { cancelled = true; };
  }, [leagueId, result, teamMates]);

  // Available players — everyone in the league who registered without a team.
  // Loaded on demand rather than with the roster: a captain with a full team
  // never opens it, and the list is only interesting once they need a player.
  const [freeAgents, setFreeAgents] = useState(null);   // null = not loaded yet
  const [agentsBusy, setAgentsBusy] = useState(false);

  const loadFreeAgents = async () => {
    if (!myTeam) return;
    setAgentsBusy(true); setTeamMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'manageLeagueTeam');
      const { data } = await fn({ teamId: myTeam.id, action: 'freeAgents' });
      setFreeAgents(data.available || []);
    } catch (e) {
      logError('Leagues', e.message, e.stack);
      setTeamMsg(e.message || "Couldn't load the available players.");
    }
    setAgentsBusy(false);
  };

  const captainAction = async (payload, okMsg) => {
    setTeamBusy(true); setTeamMsg('');
    try {
      const fn = httpsCallable(getFunctions(), 'manageLeagueTeam');
      const { data } = await fn({ teamId: myTeam.id, ...payload });
      setTeamMates(data.members || []);
      if (data.name) setMyTeam(t => ({ ...t, name: data.name }));
      setTeamMsg(okMsg);
      // Handing over means this viewer stops being the captain, so the panel
      // they are looking at is no longer theirs. Refetching drops it.
      if (payload.action === 'transferCaptain') { reloadTeam(); }
      // A player just moved into or out of the pool, so a list already on
      // screen is now wrong. Refetched rather than patched: another captain may
      // have taken someone in the meantime, and that is worth seeing.
      if (freeAgents !== null && payload.action !== 'rename') {
        try {
          const fn2 = httpsCallable(getFunctions(), 'manageLeagueTeam');
          const r = await fn2({ teamId: myTeam.id, action: 'freeAgents' });
          setFreeAgents(r.data.available || []);
        } catch (e) { /* the roster change still stands */ }
      }
    } catch (e) {
      setTeamMsg(e.message || 'That did not work. Please try again.');
    }
    setTeamBusy(false);
  };

  // Returning from Stripe. The webhook is what actually marks it paid; this
  // only tells them it went through, so a slow webhook can't show a failure.
  const justPaid = params.get('paid') === '1';

  const juniors = isJuniorLeague(league);
  const juniorGaps = missingJuniorFields(league, { guardianName, birthdate });
  // The server's rule, mirrored for display only — see isActiveClubJunior in
  // functions/index.js. A free place that is quoted at $100 right up to the
  // click is the sort of price nobody waits to find out is wrong.
  const playsFree = (() => {
    if (!juniors || league?.freeForActiveJuniors !== true) return false;
    const a = myJuniors.find(j => j.id === playerAthleteId);
    if (!a || a.isArchived === true) return false;
    // Sessions left on the account, or a subscription that keeps topping them
    // up. Mirrors isActiveClubJunior in functions/index.js, which decides.
    if ((Number(a.sessionBalance) || 0) > 0) return true;
    return a.subscriptionStatus === 'active' || !!a.activePlanId;
  })();

  // Nobody should be able to register a junior league without saying who plays.
  const playerMissing = juniors && !playerAthleteId
    && !(playerFirst.trim() && playerLast.trim());
  // Only for a new player: one already on file has these on their record.
  const newPlayerInfoMissing = juniors && !playerAthleteId
    && !(ecName.trim() && ecPhone.trim() && allergies.trim());
  const playerAge = ageOn(birthdate);
  // Compared as YYYY-MM-DD strings against the club's own today, matching how
  // the server decides it — a Date built from a date-string lands a day out on
  // either side of UTC midnight.
  const seasonStarted = !!league?.startDate && league.startDate <= localToday();
  // The captain's roster call returns names only by design, so what the team is
  // short of comes from the public roster's aggregates for their team — the
  // same numbers everyone else sees, rather than a second source that could
  // disagree with the card directly above.
  const myTeamPublic = myTeam && roster?.teams?.find(t => t.id === myTeam.id);
  // Junior pairs are named by first name; adults must supply a name themselves.
  const fallbackFirstName = String(myName || '').trim().split(/\s+/)[0] || 'your first name';
  const teamNameMissing = teamChoice === NEW_TEAM && !juniors && !newTeamName.trim();
  const genderMissing = !impliedGender(league?.leagueType) && !gender;
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const register = async () => {
    setSubmitting(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'createLeagueRegistration');
      const { data } = await fn({ leagueId, phone, notes, email: email.trim(),
        gender,
        ...teamChoiceFields(teamChoice, newTeamName, joinTeamId), waiverAccepted,
        // Sent only for a junior league; the server ignores them otherwise, so
        // an adult league never records a guardian nobody asked for.
        ...(juniors ? {
          guardianName: guardianName.trim(),
          birthdate,
          // Either the athlete they picked, or the name they typed for a child
          // with no record here yet. The server re-checks the athlete is theirs.
          ...(playerAthleteId
            ? { athleteId: playerAthleteId }
            : {
              playerFirstName: playerFirst.trim(),
              playerLastName: playerLast.trim(),
              emergencyContact: { name: ecName.trim(), phone: ecPhone.trim() },
              allergies: allergies.trim(),
            }),
        } : {}) });
      if (data.url) { window.location.href = data.url; return; }
      setResult(data);
    } catch (e) {
      logError('Leagues', e.message, e.stack);
      setError(e.message || 'Registration failed. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <Layout><Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box></Layout>
  );
  if (!league) return (
    <Layout><Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">League not found.</Alert></Container></Layout>
  );

  const open = league.status === 'registration';

  return (
    <Layout>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Button onClick={() => navigate('/leagues')} sx={{ mb: 2 }}>← All leagues</Button>
        <Typography variant="h4" fontWeight="bold">{league.name}</Typography>
        {league.dayTimeDisplay && (
          <Typography variant="body1" color="text.secondary">{league.dayTimeDisplay}</Typography>
        )}

        <Card elevation={2} sx={{ borderRadius: 3, my: 3 }}>
          <CardContent>
            <Typography variant="body2"><strong>Format:</strong> {leagueTypeLabel(league)} {league.format}s
              {Number(league.rosterMax) > Number(league.format) && ` (rosters up to ${league.rosterMax})`}</Typography>
            <Typography variant="body2"><strong>Season:</strong> {league.numWeeks} weeks from {fmtDate(league.startDate)}</Typography>
            {Array.isArray(league.timeSlots) && league.timeSlots.length > 0 && (
              <Typography variant="body2"><strong>Slots:</strong>{' '}
                {league.timeSlots.map(t => `${fmtTime(t.startTime)} (${t.lengthMinutes} min)`).join(', ')}
              </Typography>
            )}
            <Typography variant="body2"><strong>Scoring:</strong> {describeLeagueFormat(league)}</Typography>
            <Typography variant="body2"><strong>Playoffs:</strong> {fmtDate(league.playoffDate)}</Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="h5">
              {playsFree ? 'Free' : `$${(((isMember && Number(league.memberPrice) > 0
                ? Number(league.memberPrice) : Number(league.price)) || 0) / 100).toFixed(2)}`}
              <Typography component="span" variant="caption" color="text.secondary"> per player</Typography>
            </Typography>
            {playsFree && (
              <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                Torch juniors in training play this season free.
              </Typography>
            )}
            {!playsFree && Number(league.memberPrice) > 0 && (
              <Typography variant="caption" color={isMember ? 'success.main' : 'text.secondary'}>
                {isMember
                  ? `Member rate applied — standard price $${((Number(league.price) || 0) / 100).toFixed(2)}`
                  : `Members pay $${(Number(league.memberPrice) / 100).toFixed(2)}`}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Who is playing. Teams are public on this page the way a tournament's
            entries are; the names come from a callable rather than a Firestore
            read, because leagueRegistrations carries a minor's date of birth
            and a guardian's name and stays admin-or-own-row in the rules. */}
        {roster && (roster.teams.length > 0 || roster.freeAgents.length > 0) && (
          <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Who's playing</Typography>
                <Typography variant="caption" color="text.secondary">
                  {roster.playerCount} player{roster.playerCount === 1 ? '' : 's'}
                  {roster.teams.length > 0 && ` · ${roster.teams.length} team${roster.teams.length === 1 ? '' : 's'}`}
                  {roster.waitlistCount > 0 && ` · ${roster.waitlistCount} wait-listed`}
                </Typography>
              </Box>

              {roster.teams.map(t => (
                <Box key={t.id} sx={{ mb: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t.lookingForPlayers && (
                      <Tooltip title="This team is looking for players">
                        <FrontHandIcon sx={{ fontSize: 16, mr: 0.5, mb: '-2px', color: 'warning.main' }} />
                      </Tooltip>
                    )}
                    {t.name}
                    {t.spotsLeft > 0 && (
                      <Typography component="span" variant="caption" color="success.main">
                        {' '}— {t.spotsLeft} spot{t.spotsLeft === 1 ? '' : 's'} left
                      </Typography>
                    )}
                    {/* What the team is short of, which is the thing a browsing
                        player can actually do something about. */}
                    {(t.shortPlayers > 0 || t.shortFemale > 0) && (
                      <Typography component="span" variant="caption" color="warning.main">
                        {' '}— needs {[
                          t.shortPlayers > 0 && `${t.shortPlayers} more player${t.shortPlayers === 1 ? '' : 's'}`,
                          t.shortFemale > 0 && `${t.shortFemale === 1 ? 'a female player' : `${t.shortFemale} female players`}`,
                        ].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t.members.length
                      ? t.members.map(m => m.name + (m.isCaptain ? ' (C)' : '')).join(', ')
                      : 'No players yet'}
                  </Typography>
                </Box>
              ))}

              {roster.freeAgents.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Looking for a team ({roster.freeAgents.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {roster.freeAgents.join(', ')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Captains can ask any of them from their team panel below.
                  </Typography>
                </>
              )}

              {roster.juniors && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Junior players are shown by first name and last initial.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {standings.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Standings</Typography>
              {standings.map((r, i) => (
                <Box key={r.teamId} sx={{
                  display: 'flex', gap: 1, py: 0.5,
                  borderBottom: i < standings.length - 1 ? '1px solid #eee' : 'none',
                  fontWeight: myReg?.teamId === r.teamId ? 700 : 400,
                }}>
                  <Typography variant="body2" sx={{ width: 24 }}>{i + 1}</Typography>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>{r.name}</Typography>
                  <Typography variant="body2">{r.wins}–{r.losses}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ width: 46, textAlign: 'right' }}>
                    {r.diff > 0 ? `+${r.diff}` : r.diff}
                  </Typography>
                  <Typography variant="body2" sx={{ width: 60, textAlign: 'right',
                    color: r.diffPct > 0 ? 'success.main' : r.diffPct < 0 ? 'error.main' : 'text.secondary' }}>
                    {fmtPointRatio(r)}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {totalRounds > 0 && (
          <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={700}>Play-off</Typography>
                {champion && (
                  <Chip size="small" color="success" label={`${champion.name} wins the league`} />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Single elimination, seeded from the standings
                {league.playoffDate ? ` · ${fmtDate(league.playoffDate)}` : ''}
              </Typography>
              {/* Rounds scroll sideways rather than squeezing: on a phone a
                  three-round draw will not fit, and a wrapped bracket stops
                  reading left-to-right. */}
              <Box sx={{ display: 'flex', gap: `${BRACKET_GAP}px`, overflowX: 'auto', mt: 1.5, pb: 1,
                alignItems: 'stretch' }}>
                {/* Grow to share the card when the rounds fit, never shrink
                    below 130. A three-round draw then needs no scrolling — the
                    score that decided the league was landing off-screen — while
                    a sixteen-team field still scrolls rather than squeezing.
                    Every match takes an equal share of its column, which is what
                    centres it between the two feeding it; the elbows assume it. */}
                {bracketRounds.map((round, r) => (
                  <Box key={r} sx={{ flex: '1 0 130px', minWidth: 130,
                    display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      {roundName(r, totalRounds)}
                    </Typography>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {round.map(m => {
                      const w = winnerOf(m);
                      const bye = isBye(m);
                      const side = (slot) => {
                        const id = slot === 'home' ? m.homeTeamId : m.awayTeamId;
                        const nm = slot === 'home' ? m.homeTeamName : m.awayTeamName;
                        const sd = slot === 'home' ? m.homeSeed : m.awaySeed;
                        const sc = slot === 'home' ? m.homeScore : m.awayScore;
                        const mine = id && myReg?.teamId === id;
                        return (
                          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', py: 0.25 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ width: 14 }}>
                              {id ? (sd ?? '') : ''}
                            </Typography>
                            <Typography variant="body2" sx={{
                              flexGrow: 1,
                              fontWeight: w?.id === id || mine ? 700 : 400,
                              color: id ? 'text.primary' : 'text.disabled',
                              // A long name wraps rather than being truncated or
                              // forcing the bracket to scroll — two tight lines
                              // beat an ellipsis on the team that won.
                              lineHeight: 1.25,
                            }}>
                              {/* An empty side in round one is a bye, not an
                                  opponent still to be decided. */}
                              {nm || (bye ? 'Bye' : 'TBD')}
                            </Typography>
                            <Typography variant="body2" fontWeight={w?.id === id ? 700 : 400}>
                              {sc ?? ''}
                            </Typography>
                          </Box>
                        );
                      };
                      return (
                        <Box key={m.id} sx={{ flex: 1, display: 'flex', alignItems: 'center',
                          position: 'relative' }}>
                          {r > 0 && <BracketConnector />}
                          <Paper elevation={1} sx={{
                            width: '100%', p: 1, borderRadius: 2,
                            borderLeft: w ? '3px solid #27AE60' : '3px solid transparent',
                          }}>
                            {side('home')}
                            {side('away')}
                          </Paper>
                        </Box>
                      );
                    })}
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {myReg?.teamName && (
          <Alert severity="success" sx={{ mb: 2 }}>
            You're on <strong>{myReg.teamName}</strong>.
          </Alert>
        )}

        {/* A captain asked for you. Answering is yours to do — nothing moved
            when they asked, so declining costs you nothing and you stay on the
            available list either way until you say yes. */}
        {myReg?.pendingTeamId && !myReg?.teamId && (
          <Alert severity="info" sx={{ mb: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" color="inherit" disabled={inviteBusy}
                  onClick={() => answerInvite('declineInvite')}>No thanks</Button>
                <Button size="small" variant="contained" disabled={inviteBusy}
                  onClick={() => answerInvite('acceptInvite')}>Join</Button>
              </Box>
            }>
            <strong>{myReg.pendingTeamName || 'A team'}</strong> asked you to join them.
          </Alert>
        )}
        {inviteMsg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInviteMsg('')}>{inviteMsg}</Alert>}

        {justPaid && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Payment received — you're registered. A confirmation email is on its way.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {result?.alreadyRegistered && (
          <Alert severity="info" sx={{ mb: 2 }}>You're already registered for this league.</Alert>
        )}
        {result?.status === 'waitlisted' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This league is full. You're on the wait-list at position {result.waitlistPosition} —
            we'll be in touch if a spot opens, and you haven't been charged.
          </Alert>
        )}
        {result && result.status === 'registered' && result.paid && (
          <Alert severity="success" sx={{ mb: 2 }}>You're registered.</Alert>
        )}

        {/* Someone already in the league is shown where they stand, not the form
            they have already filled in. This is also what you come back to from
            Stripe: the redirect reloads the page, so `result` is gone and the
            form would otherwise reappear directly under "Payment received". */}
        {myReg ? (
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {(() => {
                  // A parent looking at their child's registration should read the
                  // child's name, not "you're registered" — they are not playing.
                  const who = myReg.viaEmail
                    ? `${myReg.firstName || ''} ${myReg.lastName || ''}`.trim()
                    : '';
                  if (myReg.status === 'waitlisted') {
                    return who ? `${who} is on the wait-list` : "You're on the wait-list";
                  }
                  return who ? `${who} is registered` : "You're registered";
                })()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {myReg.status === 'waitlisted'
                  ? `Position ${myReg.waitlistPosition || '—'}. We'll be in touch if a spot opens, and you haven't been charged.`
                  : myReg.teamName
                    ? `Playing for ${myReg.teamName}.`
                    : 'Registered as a free agent — the club will place you on a team.'}
              </Typography>
              {myReg.paymentStatus === 'pending' && (
                <Alert severity="warning" sx={{ mt: 2 }}
                  action={
                    <Button color="inherit" size="small" disabled={payBusy} onClick={payNow}>
                      {payBusy ? 'Opening…' : 'Pay now'}
                    </Button>
                  }>
                  Your payment hasn't come through yet — your place is held, but it isn't
                  paid for. Pay now picks up where the checkout page left off.
                </Alert>
              )}
              {myReg.paymentStatus === 'paid' && Number(myReg.amountPaid) > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Paid ${(Number(myReg.amountPaid) / 100).toFixed(2)}.
                </Typography>
              )}

              {/* Captain's controls, on the page they already visit. A separate
                  page would be one more thing to find and link to. */}
              {myTeam && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    You're the captain of {myTeam.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Rename the team, ask players who are waiting for one, and drop
                    anyone who joined by mistake. Removing someone leaves them
                    registered — they go back on the available list.
                  </Typography>
                  {seasonStarted ? (
                    // The server refuses this once the season starts; saying so
                    // here means a captain reads it instead of discovering it by
                    // typing a new name and being turned down.
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      The season has started, so <strong>{myTeam.name}</strong> is renamed by the
                      club now — ask an admin if it needs changing.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <TextField size="small" label="Team name" value={renaming}
                        onChange={e => setRenaming(e.target.value)} sx={{ flex: '1 1 200px' }} />
                      <Button variant="outlined" disabled={teamBusy
                        || !renaming.trim() || renaming.trim() === myTeam.name}
                        onClick={() => captainAction({ action: 'rename', name: renaming.trim() }, 'Team renamed.')}>
                        Save
                      </Button>
                    </Box>
                  )}
                  {/* Wanting someone is not the same as having a gap: a team can
                      be short and not want a stranger, or full and still want a
                      sub. So it is asked, not inferred from the roster count. */}
                  <FormControlLabel
                    sx={{ display: 'block', mb: 2 }}
                    control={
                      <Switch size="small" disabled={teamBusy}
                        checked={myTeam.lookingForPlayers === true}
                        onChange={e => {
                          const on = e.target.checked;
                          setMyTeam(t => ({ ...t, lookingForPlayers: on }));
                          captainAction(
                            { action: 'lookingForPlayers', lookingForPlayers: on },
                            on ? 'Your team is flagged as looking for players.'
                               : 'Flag removed.');
                        }} />
                    }
                    label={
                      <Typography variant="body2">
                        Looking for players
                        <Typography component="span" variant="caption" sx={{ color: '#888', display: 'block' }}>
                          Shows a hand next to your team on the league page.
                        </Typography>
                      </Typography>
                    } />
                  {teamMates.map(m => (
                    <Box key={m.registrationId}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {m.name}{m.isCaptain && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}— captain
                          </Typography>
                        )}
                      </Typography>
                      {!m.isCaptain && (
                        <>
                          <Button size="small" color="inherit" disabled={teamBusy}
                            onClick={() => {
                              if (!window.confirm(
                                `Make ${m.name} captain of ${myTeam.name}?\n\n`
                                + 'They take over renaming the team and managing the roster. '
                                + 'You stay on the team as a player, and cannot undo this yourself.')) return;
                              captainAction(
                                { action: 'transferCaptain', registrationId: m.registrationId },
                                `${m.name} is now the captain.`);
                            }}>
                            Make captain
                          </Button>
                          <Button size="small" color="inherit" disabled={teamBusy}
                            onClick={() => captainAction(
                              { action: 'removePlayer', registrationId: m.registrationId },
                              `${m.name} removed from the team.`)}>
                            Remove
                          </Button>
                        </>
                      )}
                    </Box>
                  ))}
                  {myTeamPublic && (
                    <Typography variant="body2" sx={{ mb: 1.5 }}
                      color={myTeamPublic.shortPlayers === 0 && myTeamPublic.shortFemale === 0
                        ? 'success.main' : 'warning.main'}>
                      {myTeamPublic.shortPlayers === 0 && myTeamPublic.shortFemale === 0
                        ? `Your team is set — ${myTeamPublic.members.length} of ${league.rosterMax || league.format}.`
                        : `Needs ${[
                            myTeamPublic.shortPlayers > 0
                              && `${myTeamPublic.shortPlayers} more player${myTeamPublic.shortPlayers === 1 ? '' : 's'}`,
                            myTeamPublic.shortFemale > 0
                              && `${myTeamPublic.shortFemale === 1 ? 'at least 1 female' : `at least ${myTeamPublic.shortFemale} female`}`,
                          ].filter(Boolean).join(', ')} to be able to play.`}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  {freeAgents === null ? (
                    <Button size="small" variant="outlined" disabled={agentsBusy} onClick={loadFreeAgents}>
                      {agentsBusy ? 'Loading…' : 'Find players for my team'}
                    </Button>
                  ) : (
                    <>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                        Available players
                      </Typography>
                      {freeAgents.length === 0 ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Nobody is waiting for a team right now. Check back — players
                          who register without one show up here.
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Registered for this league with no team yet. Asking sends
                            them an invitation — they join once they accept.
                          </Typography>
                          {freeAgents.map(a => (
                            <Box key={a.registrationId}
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                {a.name}
                                {a.invitedByYou && (
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    {' '}— asked, waiting on them
                                  </Typography>
                                )}
                                {a.invitedElsewhere && (
                                  <Typography component="span" variant="caption" color="warning.main">
                                    {' '}— another team asked first
                                  </Typography>
                                )}
                              </Typography>
                              <Button size="small" variant="outlined" disabled={teamBusy || a.invitedByYou}
                                onClick={() => captainAction(
                                  { action: 'invitePlayer', registrationId: a.registrationId },
                                  `Asked ${a.name} to join ${myTeam.name}. They choose whether to accept.`)}>
                                {a.invitedByYou ? 'Asked' : 'Ask'}
                              </Button>
                            </Box>
                          ))}
                        </>
                      )}
                      <Button size="small" color="inherit" disabled={agentsBusy}
                        onClick={loadFreeAgents} sx={{ mt: 0.5 }}>
                        {agentsBusy ? 'Refreshing…' : 'Refresh'}
                      </Button>
                    </>
                  )}
                  {teamMsg && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {teamMsg}
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ) : !open ? (
          <Alert severity="info">Registration isn't open for this league.</Alert>
        ) : !user ? (
          // Two ways in, deliberately. Signing in is the better deal for a
          // member — the member rate can only be applied to an account we can
          // check — but requiring it turns a two-minute sign-up into "create an
          // account first", which is where people leave.
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Register</Typography>
              <Button variant="contained" fullWidth
                onClick={() => navigate(`/league-signup?league=${leagueId}`)}>
                Register without an account
              </Button>
              <Divider sx={{ my: 2 }}>or</Divider>
              {/* Carries the return path: afterLoginTarget() reads ?next= and
                  sends them back here, rather than to the dashboard. */}
              <Button variant="outlined" fullWidth
                onClick={() => navigate(`/login?next=${encodeURIComponent(`/leagues/${leagueId}`)}`)}>
                Sign in
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {Number(league.memberPrice) > 0
                  ? `Members pay $${(Number(league.memberPrice) / 100).toFixed(2)} — sign in to get that rate.`
                  : 'Signing in keeps this league with the rest of your account.'}
              </Typography>
            </CardContent>
          </Card>
        ) : !result ? (
          <Card elevation={1} sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Register</Typography>
              <TextField fullWidth size="small" required label="Email" type="email"
                value={email} onChange={e => setEmail(e.target.value)} sx={{ mb: 2 }}
                error={!!email.trim() && !emailOk}
                helperText={email.trim() && !emailOk
                  ? 'Check this address'
                  : 'Where your confirmation and receipt go'} />
              <TextField fullWidth size="small" required label="Phone" value={phone}
                onChange={e => setPhone(e.target.value)} sx={{ mb: 2 }}
                helperText="So we can reach you about match nights" />
              {juniors && (
                <>
                  <TextField fullWidth size="small" select label="Who is playing?"
                    value={playerAthleteId} onChange={e => setPlayerAthleteId(e.target.value)}
                    sx={{ mb: 2 }} SelectProps={{ displayEmpty: true }}
                    InputLabelProps={{ shrink: true }}
                    helperText={myJuniors.length
                      ? 'The player, not the parent — this is the name on the roster'
                      : 'No juniors on your account yet, so type the player below'}>
                    {myJuniors.map(j => (
                      <MenuItem key={j.id} value={j.id}>{j.firstName} {j.lastName}</MenuItem>
                    ))}
                    <MenuItem value="">Someone else…</MenuItem>
                  </TextField>
                  {!playerAthleteId && (
                    <>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField fullWidth size="small" required label="Player's first name"
                          value={playerFirst} onChange={e => setPlayerFirst(e.target.value)} />
                        <TextField fullWidth size="small" required label="Player's last name"
                          value={playerLast} onChange={e => setPlayerLast(e.target.value)} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField fullWidth size="small" required label="Emergency contact"
                          value={ecName} onChange={e => setEcName(e.target.value)}
                          helperText="Who we call if we cannot reach you" />
                        <TextField fullWidth size="small" required label="Their phone"
                          value={ecPhone} onChange={e => setEcPhone(e.target.value)} />
                      </Box>
                      <TextField fullWidth size="small" required label="Allergies" sx={{ mb: 2 }}
                        value={allergies} onChange={e => setAllergies(e.target.value)}
                        helperText="Enter None if there are none" />
                    </>
                  )}
                  <TextField fullWidth size="small" required label="Parent or guardian"
                    value={guardianName} onChange={e => setGuardianName(e.target.value)}
                    sx={{ mb: 2 }} helperText="Who we contact about this player" />
                  <TextField fullWidth size="small" required type="date"
                    label="Player's date of birth" value={birthdate}
                    onChange={e => setBirthdate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: localToday() }}
                    sx={{ mb: 2 }}
                    helperText={playerAge != null ? `${playerAge} years old` : 'Used for age groups'} />
                </>
              )}
              {!impliedGender(league?.leagueType) && (
                <TextField fullWidth size="small" select label={genderLabel(juniors)} value={gender}
                  onChange={e => setGender(e.target.value)} sx={{ mb: 2 }}
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                  helperText="Used to build balanced teams">
                  <MenuItem value="">Select…</MenuItem>
                  {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </TextField>
              )}
              <TextField fullWidth size="small" select label="Team" value={teamChoice}
                onChange={e => setTeamChoice(e.target.value)} sx={{ mb: 0.5 }}>
                <MenuItem value={FREE_AGENT}>Free agent — place me on a team</MenuItem>
                <MenuItem value={JOIN_TEAM}>Join an existing team</MenuItem>
                <MenuItem value={NEW_TEAM}>Start a new team — I'm the captain</MenuItem>
              </TextField>
              {teamChoice === JOIN_TEAM && (
                <>
                  <TextField fullWidth size="small" select label="Which team? (optional)"
                    value={joinTeamId} onChange={e => setJoinTeamId(e.target.value)}
                    SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                    sx={{ mt: 1.5, mb: 0.5 }}>
                    <MenuItem value="">Not sure yet — put me on the available list</MenuItem>
                    {teamOptions.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                  </TextField>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    {joinTeamId
                      ? 'You will be added to that team when you register.'
                      : "Leave this blank and your name goes on the league's available players list, where any captain can pick you up."}
                  </Typography>
                </>
              )}
              {teamChoice === NEW_TEAM ? (
                <TextField fullWidth size="small" required={!juniors}
                  label={juniors ? 'Team name (optional)' : 'Team name'}
                  value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
                  error={teamNameMissing && newTeamName !== ''}
                  sx={{ mt: 1.5, mb: 2 }}
                  helperText={newTeamName.trim()
                    ? "You can change this later — you're the captain."
                    : juniors
                      ? `Leave blank and the team is named after its players — ${fallbackFirstName} until a partner joins, then both first names.`
                      : 'Every 4s team needs a name. You can change it later.'} />
              ) : teamChoice === JOIN_TEAM ? null : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  The club will place you — a captain can also pick you off the
                  available players list.
                </Typography>
              )}
              <TextField fullWidth size="small" label="Anything we should know? (optional)"
                multiline minRows={2} value={notes} onChange={e => setNotes(e.target.value)} sx={{ mb: 2 }} />
              {waiver?.unreadable && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This league's waiver can't be shown here. Please contact the club to register.
                </Alert>
              )}
              {waiver && !waiver.unreadable && (
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', mb: 1,
                    '.MuiFormControlLabel-label': { fontSize: '0.82rem' } }}
                  control={<Checkbox checked={waiverAccepted}
                    onChange={e => setWaiverAccepted(e.target.checked)} sx={{ pt: 0 }} />}
                  label={<span>
                    I have read and accept the{' '}
                    <Link href={waiverHref(waiver)} target="_blank" rel="noopener">{waiver.name}</Link>.
                  </span>} />
              )}
              <Button variant="contained" fullWidth
                disabled={submitting || !phone.trim() || !emailOk || juniorGaps.length > 0
                  || playerMissing || newPlayerInfoMissing || teamNameMissing || genderMissing
                  || (waiver && (!waiverAccepted || waiver.unreadable))} onClick={register}>
                {submitting ? 'Working…'
                  : playsFree ? 'Register — free'
                    : ((isMember && Number(league.memberPrice) > 0 ? Number(league.memberPrice) : Number(league.price)) > 0
                      ? `Register — $${(((isMember && Number(league.memberPrice) > 0
                          ? Number(league.memberPrice) : Number(league.price)) || 0) / 100).toFixed(2)}`
                      : 'Register')}
              </Button>
              {/* Why the price is lower. A discount nobody can account for gets
                  queried at the desk; naming the junior answers it up front. */}
              {isMember && Number(league.memberPrice) > 0 && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                  {juniorBasis
                    ? `Member rate — ${juniorBasis.name || 'your junior'} has `
                      + (juniorBasis.comped ? 'a comped account.' : `${juniorBasis.sessionBalance} sessions.`)
                    : 'Member rate applied.'}
                </Typography>
              )}
            </CardContent>
          </Card>
        ) : null}
      </Container>
    </Layout>
  );
}
